import { ReactNode, Ref, useEffect, useImperativeHandle, useRef } from "react";

import css from "./chat_body.module.scss";

type ChatBodyHandle = {
  isBottom: () => boolean;
  savePosition: () => void;
  restorePosition: () => void;
  cleanScrollPosition: () => void;
  hasSavedScrollPosition: () => boolean;
};

type ChatBodyProps = {
  top?: number;
  bottom?: number;
  opacity?: number;
  children?: ReactNode;
  onScroll?: () => void;
  ref?: Ref<ChatBodyHandle>;
};

export const ChatBody = ({ opacity = 1, top = 32, bottom = 0, children, onScroll, ref }: ChatBodyProps) => {
  const wrap = useRef<HTMLDivElement>(null);
  const prevHeight = useRef(0);
  const prevScrollTop = useRef(0);

  useEffect(() => {
    const wrapElement = wrap.current;
    if (!wrapElement || !onScroll) {
      return;
    }
    wrapElement.addEventListener("scroll", onScroll, { passive: true });
    return () => wrapElement.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useImperativeHandle(ref, () => ({
    isBottom: () => {
      const wrapElement = wrap.current;
      return !!wrapElement && wrapElement.scrollTop + wrapElement.offsetHeight === wrapElement.scrollHeight;
    },
    savePosition: () => {
      if (!wrap.current) {
        return;
      }
      prevHeight.current = wrap.current.scrollHeight;
      prevScrollTop.current = wrap.current.scrollTop;
    },
    restorePosition: () => {
      const wrapElement = wrap.current;
      if (!wrapElement) {
        return;
      }
      wrapElement.scrollTop = wrapElement.scrollHeight - prevHeight.current + prevScrollTop.current;
    },
    cleanScrollPosition: () => {
      prevHeight.current = 0;
      prevScrollTop.current = 0;
    },
    hasSavedScrollPosition: () => prevHeight.current > 0 && prevScrollTop.current > 0
  }));

  return (
    <div ref={wrap} className={css.root} style={{ opacity, top, bottom }} data-qa-id="chat-window-body">
      {children}
    </div>
  );
};
